import React, { useState, useEffect, useContext } from "react";
import { Container, Spinner } from "react-bootstrap";
import BreadcrumbComp from "../layouts/BreadcrumbComp";
import GridMaster from "../masters/GridMaster";
import ActionComp from "../layouts/ActionComp";
import ComparisonSheet from "./ComparisonSheet";
import ComparisonSheetPDFModal from "./ComparisonSheetPDFModal";
import { AuthContext } from "../../contexts/AuthContext";
import { menuActions } from "../../store/menu-slice";
import { useDispatch } from "react-redux";
import { searchActions } from "../../store/grid-slice";
import { formatDate, formatDateInDDMMYYYY, formatDateInYYYYMMDD } from "../../functions/common-functions";
import { getEnquiriesForFinalize } from "../../services/finalize-bid-service";
import { getPresignedURL } from "../../functions/upload";
import "./FinalizeBid.css";
import ActionRenderer from "../masters/ActionRenderer";
import { exportToExcel } from "../utils/ExcelCreation";
import { getComparisonSheetData } from "../../services/finalize-bid-service";

const FinalizeBid = () => {
  const context = useContext(AuthContext);
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [excelData, setExcelData] = useState([]);
  const [resetFilter, setResetFilter] = useState(true);
  const [showComparisonSheet, setShowComparisonSheet] = useState(false);
  const [showPDFModal, setShowPDFModal] = useState(false);
  const [selectedEnquiry, setSelectedEnquiry] = useState(null);
  const [reload, setReload] = useState(false);
  const [comparisonData, setComparisonData] = useState(null);

  useEffect(() => {
    dispatch(menuActions.setMenu("Finalize Bid"));
    dispatch(searchActions.setSearch(""));
  }, [dispatch]);

  useEffect(() => {
    fetchEnquiries();
  }, [reload]);

  const fetchEnquiries = async () => {
    setLoading(true);
    try {
      const enquiries = await getEnquiriesForFinalize();
      // Filter enquiries with Due Date Past status and not frozen/rejected/deleted
      const filteredEnquiries = enquiries.filter(enquiry =>
        enquiry.EnquiryStatus === "Live" &&
        enquiry.Flag === "Active" &&
        !["Frozen", "Closed", "Rejected", "Deleted"].includes(enquiry.EnquiryStatus)
      );
      setData(filteredEnquiries);
      setFilteredData(filteredEnquiries);
      setExcelData(filteredEnquiries);
      setResetFilter(!resetFilter);
    } catch (error) {
      console.error("Error fetching enquiries:", error);
    }
    setLoading(false);
  };

  const handleFileDownload = async (enquiryNumber, fileName) => {
    try {
      const filePath = `EnquiryTender/${enquiryNumber.replace(/\//g, '_')}/${fileName}`;
      const downloadURL = await getPresignedURL(filePath);
      if (downloadURL) {
        window.open(downloadURL, '_blank');
      }
    } catch (error) {
      console.error("Error downloading file:", error);
    }
  };

  const handlePDFDownload = async (enquiryData) => {
    try {
      setLoading(true);
      // Find the enquiry in filtered data to ensure we have the correct context
      const enquiryInFilteredData = filteredData.find(item => item.EnquiryNumber === enquiryData.EnquiryNumber);
      const enquiryToUse = enquiryInFilteredData || enquiryData;
      
      const data = await getComparisonSheetData({ EnquiryNumber: enquiryToUse.EnquiryNumber });

      // Sort customers by bid amount (highest first), handling different data types
      const sortedCustomers = [...(data.customers || [])].sort((a, b) => {
        const bidA = a.CurrentBid === "Regret" || a.CurrentBid === "No Bid Placed" ? -1 : parseFloat(a.CurrentBid) || 0;
        const bidB = b.CurrentBid === "Regret" || b.CurrentBid === "No Bid Placed" ? -1 : parseFloat(b.CurrentBid) || 0;
        if (bidA === -1 && bidB === -1) return 0;
        if (bidA === -1) return 1;
        if (bidB === -1) return -1;
        return bidB - bidA;
      });

      setSelectedEnquiry({
        enquiryData: data.enquiryData,
        comparisonRowData: sortedCustomers,
        lnCompany: data.enquiryData?.LNCompany || "",
        enquiryRemarks: ""
      });
      setShowPDFModal(true);
    } catch (error) {
      console.error("Error fetching comparison data for PDF:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleViewComparisonSheet = async (row) => {
    setLoading(true);
    try {
      // Find the enquiry in filtered data to ensure we have the correct context
      const enquiryInFilteredData = filteredData.find(item => item.EnquiryNumber === row.EnquiryNumber);
      const enquiryToUse = enquiryInFilteredData || row;
      
      console.log('Fetching comparison data for:', enquiryToUse.EnquiryNumber);
      const data = await getComparisonSheetData({ EnquiryNumber: enquiryToUse.EnquiryNumber });
      console.log('Comparison data fetched:', data);
      
      // Sort customers by bid amount (highest first)
      const sortedCustomers = [...(data.customers || [])].sort((a, b) => {
        const bidA = a.CurrentBid === "Regret" || a.CurrentBid === "No Bid Placed" ? -1 : parseFloat(a.CurrentBid) || 0;
        const bidB = b.CurrentBid === "Regret" || b.CurrentBid === "No Bid Placed" ? -1 : parseFloat(b.CurrentBid) || 0;
        if (bidA === -1 && bidB === -1) return 0;
        if (bidA === -1) return 1;
        if (bidB === -1) return -1;
        return bidB - bidA;
      });
      
      setComparisonData({
        enquiryData: data.enquiryData,
        customers: sortedCustomers
      });
      setSelectedEnquiry(enquiryToUse);
      setShowComparisonSheet(true);
    } catch (error) {
      console.error('Error fetching comparison data:', error);
    }
    setLoading(false);
  };

  const getEnquirySource = (enquiry) => {
    if (enquiry.EnquirySource === "Disposal") return "Disposal";
    if (enquiry.EnquirySource === "Contract") return "Contract";
    return "Manual";
  };

  const columnDefs = [
    {
      headerName: "Enquiry Number",
      field: "EnquiryNumber",
      minWidth: 160,
      tooltipField: "EnquiryNumber",
    },
    {
      headerName: "Enquiry Date",
      field: "InsertedOn",
      minWidth: 150,
      tooltipField: "InsertedOn",
      filter: "agDateColumnFilter",
      valueFormatter: (params) => {
        return params.value ? formatDate(params.value) : "";
      },
      filterParams: {
        comparator: (filterLocalDateAtMidnight, cellValue) => {
          const dateAsString = cellValue;
          if (dateAsString == null) {
            return 0;
          }
          const dateCell = new Date(formatDateInYYYYMMDD(cellValue) + " 00:00:00");

          if (dateCell < filterLocalDateAtMidnight) {
            return -1;
          } else if (dateCell > filterLocalDateAtMidnight) {
            return 1;
          }
          return 0;
        },
      },
    },
    {
      headerName: "Due Date",
      field: "DueDate",
      minWidth: 120,
      tooltipField: "DueDate",
      valueFormatter: (params) => {
        return params.value ? formatDateInDDMMYYYY(params.value.toString()) : "";
      },
      filter: "agDateColumnFilter",
      filterParams: {
        comparator: (filterLocalDateAtMidnight, cellValue) => {
          const dateAsString = cellValue;
          if (dateAsString == null) {
            return 0;
          }
          const dateCell = new Date(formatDateInYYYYMMDD(cellValue) + " 00:00:00");

          if (dateCell < filterLocalDateAtMidnight) {
            return -1;
          } else if (dateCell > filterLocalDateAtMidnight) {
            return 1;
          }
          return 0;
        },
      },
    },
    {
      headerName: "Item Code",
      field: "ItemCode",
      minWidth: 120,
      tooltipField: "ItemCode",
    },
    {
      headerName: "Item Description",
      field: "ItemDescription",
      minWidth: 150,
      tooltipField: "ItemDescription",
    },
    {
      headerName: "Item Detail",
      field: "ItemDetail",
      minWidth: 150,
      tooltipField: "ItemDetail",
    },
    {
      headerName: "Item Type",
      field: "ItemType",
      minWidth: 120,
      tooltipField: "ItemType",
    },
    {
      headerName: "Quantity",
      field: "Quantity",
      minWidth: 100,
      tooltipField: "Quantity",
    },
    {
      headerName: "UOM",
      field: "UOM",
      minWidth: 90,
      tooltipField: "UOM",
    },
    {
      headerName: "GST%",
      field: "GST",
      minWidth: 90,
      tooltipField: "GST",
    },
    {
      headerName: "Product Class",
      field: "ProductClass",
      minWidth: 120,
      tooltipField: "ProductClass",
      valueGetter: (params) => {
        if (params.data.EnquirySource === "Disposal") {
          return params.data.ProductClass || "-";
        }
        return "-";
      }
    },
    {
      headerName: "Commodity Code",
      field: "CommodityCode",
      minWidth: 130,
      tooltipField: "CommodityCode",
      valueGetter: (params) => {
        if (params.data.EnquirySource === "Disposal") {
          return params.data.CommodityCode || "-";
        }
        return "-";
      }
    },
    {
      headerName: "Division",
      field: "Division",
      minWidth: 100,
      tooltipField: "Division",
    },
    {
      headerName: "Location",
      field: "Location",
      minWidth: 120,
      tooltipField: "Location",
    },
    {
      headerName: "LN Company",
      field: "LNCompany",
      minWidth: 100,
      tooltipField: "LNCompany",
    },
    {
      headerName: "Attachment 1",
      field: "AttachImage",
      minWidth: 120,
      cellRenderer: (params) => {
        return params.value && params.data.ShowAttachImage ? (
          <a href="#" style={{ color: '#810055' }} onClick={(e) => { e.preventDefault(); handleFileDownload(params.data.EnquiryNumber, params.value); }}>
            {params.value}
          </a>
        ) : "";
      }
    },
    {
      headerName: "Attachment 2",
      field: "AttachDocument1",
      minWidth: 120,
      cellRenderer: (params) => {
        return params.value && params.data.ShowAttachDocument1 ? (
          <a href="#" style={{ color: '#810055' }} onClick={(e) => { e.preventDefault(); handleFileDownload(params.data.EnquiryNumber, params.value); }}>
            {params.value}
          </a>
        ) : "";
      }
    },
    {
      headerName: "Attachment 3",
      field: "AttachDocument2",
      minWidth: 120,
      cellRenderer: (params) => {
        return params.value && params.data.ShowAttachDocument2 ? (
          <a href="#" style={{ color: '#810055' }} onClick={(e) => { e.preventDefault(); handleFileDownload(params.data.EnquiryNumber, params.value); }}>
            {params.value}
          </a>
        ) : "";
      }
    },
    {
      headerName: "Attachment 4",
      field: "AttachDocument3",
      minWidth: 120,
      cellRenderer: (params) => {
        return params.value && params.data.ShowAttachDocument3 ? (
          <a href="#" style={{ color: '#810055' }} onClick={(e) => { e.preventDefault(); handleFileDownload(params.data.EnquiryNumber, params.value); }}>
            {params.value}
          </a>
        ) : "";
      }
    },
    {
      headerName: "Attachment 5",
      field: "ImagesAttachment",
      minWidth: 120,
      cellRenderer: (params) => {
        return params.value && (params.data.ShowImageToCustomer || params.data.EnquirySource === "Manual") ? (
          <a href="#" style={{ color: '#810055' }} onClick={(e) => { e.preventDefault(); handleFileDownload(params.data.EnquiryNumber, params.value); }}>
            {params.value}
          </a>
        ) : "";
      }
    },
    {
      headerName: "Enquiry Source",
      field: "EnquirySource",
      minWidth: 130,
      valueGetter: (params) => getEnquirySource(params.data)
    },
    {
      headerName: "Enquiry Source Reference",
      field: "EnquirySourceReference",
      minWidth: 180,
      valueGetter: (params) => {
        if (params.data.EnquirySource === "Disposal") {
          return params.data.EnquirySourceNumber || "-";
        }
        return "-";
      }
    },
    {
      headerName: "Linked Customers",
      field: "LinkedBPName",
      minWidth: 200,
      tooltipField: "LinkedBPName",
    },
    {
      headerName: "Actions",
      field: "",
      width: 100,
      cellRenderer: (params) => (
        <ActionRenderer
          selectedData={params.data}
          handleViewComparisonSheet={() => handleViewComparisonSheet(params.data)}
          handleDownloadEvent={() => handlePDFDownload(params.data)}
        />
      ),
      sortable: false,
      filter: false,
      pinned: "right",
      resizable: false,
    }
  ];

  const headerName = (field) => {
    const mapping = {
      "EnquiryNumber": "Enquiry Number",
      "EnquiryDate": "Enquiry Date",
      "ItemCode": "Item Code",
      "ItemDescription": "Item Description",
      "ItemDetail": "Item Detail",
      "ItemType": "Item Type",
      "Quantity": "Quantity",
      "UOM": "UOM",
      "GST": "GST%",
      "ProductClass": "Product Class",
      "CommodityCode": "Commodity Code",
      "Division": "Division",
      "Location": "Location",
      "LNCompany": "LN Company",
      "AttachImage": "Attachment 1",
      "AttachDocument1": "Attachment 2",
      "AttachDocument2": "Attachment 3",
      "AttachDocument3": "Attachment 4",
      "ImagesAttachment": "Attachment 5",
      "EnquirySource": "Enquiry Source",
      "EnquirySourceReference": "Enquiry Source Reference",
      "DueDate": "Due Date",
      "LinkedBPName": "Linked Customers"
    };
    return mapping[field] || "No match found";
  };

  const fieldName = (header) => {
    const mapping = {
      "Enquiry Number": "EnquiryNumber",
      "Enquiry Date": "EnquiryDate",
      "Item Code": "ItemCode",
      "Item Description": "ItemDescription",
      "Item Detail": "ItemDetail",
      "Item Type": "ItemType",
      "Quantity": "Quantity",
      "UOM": "UOM",
      "GST%": "GST",
      "Product Class": "ProductClass",
      "Commodity Code": "CommodityCode",
      "Division": "Division",
      "Location": "Location",
      "LN Company": "LNCompany",
      "Attachment 1": "AttachImage",
      "Attachment 2": "AttachDocument1",
      "Attachment 3": "AttachDocument2",
      "Attachment 4": "AttachDocument3",
      "Attachment 5": "ImagesAttachment",
      "Enquiry Source": "EnquirySource",
      "Enquiry Source Reference": "EnquirySourceReference",
      "Due Date": "DueDate",
      "Linked Customers": "LinkedBPName"
    };
    return mapping[header] || "No match found";
  };

  const handleGlobalExport = async () => {
    let fileName = "Finalize_Bid";
    let excelDownloadData = [];


    excelData.forEach(element => {
      excelDownloadData.push({
        EnquiryNumber: element.EnquiryNumber,
        EnquiryDate: element.EnquiryDate,
        ItemCode: element.ItemCode,
        ItemDescription: element.ItemDescription,
        ItemDetail: element.ItemDetail,
        ItemType: element.ItemType,
        Quantity: element.Quantity,
        UOM: element.UOM,
        GST: element.GST,
        ProductClass: element.ProductClass,
        CommodityCode: element.CommodityCode,
        Division: element.Division,
        Location: element.Location,
        LNCompany: element.LNCompany,
        AttachImage: element.AttachImage,
        AttachDocument1: element.AttachDocument1,
        AttachDocument2: element.AttachDocument2,
        AttachDocument3: element.AttachDocument3,
        ImagesAttachment: element.ImagesAttachment,
        EnquirySource: element.EnquirySource,
        EnquirySourceReference: element.EnquirySourceReference,
        DueDate: element.DueDate,
        LinkedBPName: element.LinkedBPName
      });
    });

    const colName = ["Enquiry Number", "Enquiry Date", "Item Code", "Item Description", "Item Detail", "Item Type", "Quantity", "UOM", "GST%", "Product Class", "Commodity Code", "Division", "Location", "LN Company", "Attachment 1", "Attachment 2", "Attachment 3", "Attachment 4", "Attachment 5", "Enquiry Source", "Enquiry Source Reference", "Due Date", "Linked Customers"];
    const FieldName = ["EnquiryNumber", "EnquiryDate", "ItemCode", "ItemDescription", "ItemDetail", "ItemType", "Quantity", "UOM", "GST", "ProductClass", "CommodityCode", "Division", "Location", "LNCompany", "AttachImage", "AttachDocument1", "AttachDocument2", "AttachDocument3", "ImagesAttachment", "EnquirySource", "EnquirySourceReference", "DueDate", "LinkedBPName"];
    exportToExcel(fileName, "Data", excelDownloadData, "A1:W1", colName, FieldName);
  };

  return (
    <>
      {loading && <div className="spinner-box"> <Spinner animation="border" className="spinner" /></div>}
      <BreadcrumbComp />
      <Container fluid className="main-container">
        <ActionComp
          exportEvent={() => handleGlobalExport()}
        />
        <GridMaster
          rowDetails={data}
          colDetails={columnDefs}
          fieldNames={fieldName}
          headerNames={headerName}
          getDataEvent={() => { }}
          getFilterColLength={() => { }}
          setExcelData={setExcelData}
          resetind={resetFilter}
          onFilteredDataChange={setFilteredData}
        />
      </Container>

      {showComparisonSheet && (
        <ComparisonSheet
          show={showComparisonSheet}
          onHide={() => {
            setShowComparisonSheet(false);
            setComparisonData(null);
          }}
          enquiry={selectedEnquiry}
          setReload={setReload}
          reload={reload}
          comparisonData={comparisonData}
        />
      )}

      {showPDFModal && (
        <ComparisonSheetPDFModal
          show={showPDFModal}
          onHide={() => setShowPDFModal(false)}
          enquiryData={selectedEnquiry?.enquiryData}
          comparisonRowData={selectedEnquiry?.comparisonRowData || []}
          lnCompany={selectedEnquiry?.lnCompany || ""}
          enquiryRemarks={selectedEnquiry?.enquiryRemarks || ""}
        />
      )}
    </>
  );
};

export default FinalizeBid;